/**
 * −/+ quantity stepper (apply bar dark variant + light rows on mobile) with
 * DIRECT ENTRY (round 3): clicking the number swaps it for an inline numeric
 * input — Enter/blur commits (clamped to [min, max]), Escape cancels. Shared
 * everywhere qty steppers exist (Configure, Order pane, cart, frame dialog,
 * crop editor) via QtyValue.
 */

import { useEffect, useRef, useState } from 'react';
import { parseQtyInput } from '../../lib/util';

export interface QtyValueProps {
  value: number;
  min: number;
  max: number;
  onCommit: (next: number) => void;
  ariaLabel: string;
  /** Class(es) for both the display button and the edit input. */
  className: string;
}

/** Click-to-type stepper number: Enter/blur commit, Esc cancels. */
export function QtyValue({ value, min, max, onCommit, ariaLabel, className }: QtyValueProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const begin = () => {
    cancelledRef.current = false;
    setDraft(String(value));
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (cancelledRef.current) return;
    const parsed = parseQtyInput(draft, min, max);
    if (parsed != null && parsed !== value) onCommit(parsed);
  };

  if (!editing) {
    return (
      <button
        type="button"
        className={`${className} qty__edit`}
        title="Click to type a quantity"
        aria-label={`${ariaLabel}, click to type`}
        onClick={begin}
      >
        {value}
      </button>
    );
  }
  return (
    <input
      ref={inputRef}
      className={`${className} qty__inputfield`}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoFocus
      value={draft}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur(); // blur commits
        } else if (e.key === 'Escape') {
          e.stopPropagation(); // don't close the surrounding modal
          cancelledRef.current = true;
          setEditing(false);
        }
      }}
    />
  );
}

export interface QtyStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  variant?: 'dark' | 'light';
  ariaLabel: string;
}

export function QtyStepper({ value, onChange, min = 1, max = 999, variant = 'light', ariaLabel }: QtyStepperProps) {
  return (
    <span className={`qty qty--${variant}`} role="group" aria-label={ariaLabel}>
      <button
        type="button"
        className="qty__btn"
        aria-label="Decrease quantity"
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <QtyValue value={value} min={min} max={max} onCommit={onChange} ariaLabel={ariaLabel} className="qty__value" />
      <button
        type="button"
        className="qty__btn"
        aria-label="Increase quantity"
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
    </span>
  );
}
